import { useMemo, useState } from "react";
import { Scale, Info } from "lucide-react";
import Field from "../../../components/Calculator/LawyerFeeCalculator/shared/Field";
import ResultBox from "../../../components/Calculator/LawyerFeeCalculator/shared/ResultBox";
import {
  fmt,
  formatAmountInput,
} from "../../../components/Calculator/LawyerFeeCalculator/utils/format";

import HeaderRobotsBtn from "../../../components/Header/RobotsBtn";
import HeaderSupportBtn from "../../../components/Header/SupportBtn";
import HeaderProfileBtn from "../../../components/Header/ProfileBtn";
import Header from "../../../components/Header";
import ShareButton from "../../../components/ShareButton";

/* ------------------------------------------------------------------
   تم رنگی (color-primary) — دقیقاً همان پالت نارنجی LawyerFeeCalculator
------------------------------------------------------------------ */

const inputStyle = {
  width: "100%",
  borderRadius: "0.6rem",
  border: `1px solid #6b21f333`,
  background: "#FFFFFF",
  padding: "0.6rem 0.85rem",
  fontSize: "0.9rem",
  color: "#33271d",
  outline: "none",
};

const selectStyle = { ...inputStyle };

/* ------------------------------------------------------------------
   فرمول‌های دعاوی مالی — بر مبنای داده‌های تأییدشده (شعبه ۱۹ حقوقی):
   بدوی: تا ۲۰۰ میلیون ریال ۲.۵٪ / مازاد ۳.۵٪ + ۵,۰۰۰,۰۰۰ ریال ثابت
   واخواهی و تجدیدنظر: ۴.۵٪ محکوم‌به یا بهای خواسته
   اعاده دادرسی، فرجام‌خواهی و اعتراض ثالث: ۵.۵٪ محکوم‌به یا بهای خواسته
------------------------------------------------------------------ */
const feeFirstInstance = (v) =>
  v <= 200000000 ? v * 0.025 : (v - 200000000) * 0.035 + 5000000;
const feeMidTier = (v) => v * 0.045;
const feeHighTier = (v) => v * 0.055;

const STAGES = [
  { key: "boduei", label: "در مرحله بدوی", calc: feeFirstInstance },
  { key: "vakhahi", label: "در مرحله واخواهی", calc: feeMidTier },
  { key: "tajdid", label: "در مرحله تجدید نظر", calc: feeMidTier },
  { key: "eaadeh", label: "در مرحله اعاده دادرسی", calc: feeHighTier },
  { key: "farjam", label: "در مرحله فرجام خواهی", calc: feeHighTier },
  { key: "salis", label: "در مرحله اعتراض ثالث", calc: feeHighTier },
];

/* ------------------------------------------------------------------
   دعاوی غیرمالی — مقادیر ثابت دقیقاً استخراج‌شده از خروجی واقعی
   ماشین‌حساب kitset.ir (اسکرین‌شات‌های تایید شده توسط کاربر).
   نکته مهم: هر نوع دعوا فقط همان مراحلی را دارد که در سایت مبدا
   نمایش داده می‌شود (مثلاً بعضی‌ها واخواهی ندارند، بعضی‌ها فرجام‌خواهی
   دارند) — این تفاوت عمداً حفظ شده است.
------------------------------------------------------------------ */
const NON_FINANCIAL_TYPES = [
  {
    key: "gheyre-manqool",
    label: "مربوط به اموال غیرمنقول",
    stages: [
      { label: "در مرحله بدوی", value: 1000000 },
      { label: "در مرحله واخواهی", value: 1200000 },
      { label: "در مرحله تجدید نظر", value: 1500000 },
      { label: "در مرحله اعاده دادرسی", value: 1800000 },
      { label: "در مرحله اعتراض ثالث", value: 1800000 },
    ],
  },
  {
    key: "tejari-sherkat",
    label: "مربوط به امور تجاری و شرکت‌ها",
    stages: [
      { label: "در مرحله بدوی", value: 1000000 },
      { label: "در مرحله واخواهی", value: 1200000 },
      { label: "در مرحله تجدید نظر", value: 1500000 },
      { label: "در مرحله اعاده دادرسی", value: 1800000 },
      { label: "در مرحله اعتراض ثالث", value: 1800000 },
    ],
  },
  {
    key: "asnad-sejeli",
    label: "مربوط به اسناد سجلی",
    stages: [
      { label: "در مرحله بدوی", value: 750000 },
      { label: "در مرحله واخواهی", value: 1200000 },
      { label: "در مرحله تجدید نظر", value: 1500000 },
      { label: "در مرحله اعاده دادرسی", value: 1800000 },
      { label: "در مرحله اعتراض ثالث", value: 1800000 },
    ],
  },
  {
    key: "omoor-hasbi",
    label: "مربوط به امور حسبی",
    stages: [
      { label: "در مرحله بدوی", value: 750000 },
      { label: "در مرحله واخواهی", value: 1200000 },
      { label: "در مرحله تجدید نظر", value: 1500000 },
      { label: "در مرحله اعاده دادرسی", value: 1800000 },
      { label: "در مرحله فرجام خواهی", value: 1500000 },
      { label: "در مرحله اعتراض ثالث", value: 1800000 },
    ],
  },
  {
    key: "khanevadeh",
    label: "موضوع صلاحیت دادگاه خانواده و تامین خواسته و دستور موقت راجع به آن",
    stages: [
      { label: "در مرحله بدوی", value: 400000 },
      { label: "در مرحله واخواهی", value: 600000 },
      { label: "در مرحله تجدید نظر", value: 1000000 },
      { label: "در مرحله اعاده دادرسی", value: 1800000 },
      { label: "در مرحله اعتراض ثالث", value: 1800000 },
    ],
  },
  {
    key: "tamin-khaste",
    label: "درخواست تامین خواسته و دستور موقت (به استثنای امور خانواده)",
    stages: [
      { label: "در مرحله بدوی", value: 1000000 },
      { label: "در مرحله تجدید نظر", value: 1500000 },
      { label: "در مرحله اعاده دادرسی", value: 1800000 },
      { label: "در مرحله اعتراض ثالث", value: 1800000 },
    ],
  },
  {
    key: "eteraz-marja-gheyre-dadgostari",
    label: "هزینه اعتراض به آرای مراجع غیر دادگستری",
    stages: [
      { label: "در مرحله بدوی", value: 1200000 },
      { label: "در مرحله واخواهی", value: 1500000 },
      { label: "در مرحله تجدید نظر", value: 1500000 },
      { label: "در مرحله اعاده دادرسی", value: 1800000 },
      { label: "در مرحله اعتراض ثالث", value: 1800000 },
    ],
  },
  {
    key: "farjam-khahi",
    label: "دعاوی قابل فرجام خواهی",
    stages: [
      { label: "در مرحله بدوی", value: 1000000 },
      { label: "در مرحله واخواهی", value: 1500000 },
      { label: "در مرحله تجدید نظر", value: 1500000 },
      { label: "در مرحله اعاده دادرسی", value: 1800000 },
      { label: "در مرحله فرجام خواهی", value: 1500000 },
      { label: "در مرحله اعتراض ثالث", value: 1800000 },
    ],
  },
  {
    key: "sayer-gheyre-mali",
    label: "سایر دعاوی غیرمالی که در گزینه‌ها بالا نیست",
    stages: [
      { label: "در مرحله بدوی", value: 1000000 },
      { label: "در مرحله واخواهی", value: 1200000 },
      { label: "در مرحله تجدید نظر", value: 1500000 },
      { label: "در مرحله اعاده دادرسی", value: 1800000 },
      { label: "در مرحله اعتراض ثالث", value: 1800000 },
    ],
  },
];

/* ------------------------------------------------------------------
   مهریه سکه — فرمول استخراج و تایید شده از نمونه واقعی سایت
   (۴ سکه ← بدوی ۴۲,۰۰۰,۰۰۰ / واخواهی و تجدیدنظر ۵۴,۰۰۰,۰۰۰ /
   اعاده، فرجام و ثالث ۶۶,۰۰۰,۰۰۰ ریال):
   ارزش هر سکه = ۳۰۰,۰۰۰,۰۰۰ ریال (بر مبنای نرخ روز سکه بهار آزادی)
   بدوی = ارزش کل × ۳.۵٪ (بدون آستانه ۲۰۰ میلیونی دعاوی مالی)
   واخواهی / تجدیدنظر = ارزش کل × ۴.۵٪
   اعاده دادرسی / فرجام‌خواهی / اعتراض ثالث = ارزش کل × ۵.۵٪
------------------------------------------------------------------ */
const COIN_VALUE_RIAL = 300000000;
const feeCoinFirst = (v) => v * 0.035;
const feeCoinMid = (v) => v * 0.045;
const feeCoinHigh = (v) => v * 0.055;

const COIN_STAGES = [
  { key: "boduei", label: "در مرحله بدوی", calc: feeCoinFirst },
  { key: "vakhahi", label: "در مرحله واخواهی", calc: feeCoinMid },
  { key: "tajdid", label: "در مرحله تجدید نظر", calc: feeCoinMid },
  { key: "eaadeh", label: "در مرحله اعاده دادرسی", calc: feeCoinHigh },
  { key: "farjam", label: "در مرحله فرجام خواهی", calc: feeCoinHigh },
  { key: "salis", label: "در مرحله اعتراض ثالث", calc: feeCoinHigh },
];

/* ------------------------------------------------------------------
   چک بلامحل — فرمول خطی استخراج و تایید شده از ۳ نمونه واقعی سایت:
     ۲۵۰,۰۰۰,۰۰۰  ← ۹۱۰,۰۰۰
     ۴۵۰,۰۰۰,۰۰۰  ← ۱,۱۱۰,۰۰۰
     ۱,۰۰۰,۰۰۰,۰۰۰ ← ۱,۶۶۰,۰۰۰
   هزینه = مبلغ چک × ۰.۱٪ + ۶۶۰,۰۰۰ ریال (روی هر سه نمونه دقیقاً صدق می‌کند)
------------------------------------------------------------------ */
const feeBadCheck = (checkAmount) => {
  if (checkAmount <= 0) return 0;
  return checkAmount * 0.001 + 660000;
};

const toNumber = (value) => Number(String(value).replace(/,/g, "")) || 0;

const CASE_TYPES = [
  { key: "mali", label: "مالی" },
  { key: "gheyre-mali", label: "غیرمالی" },
  { key: "mehriyeh-sekeh", label: "مهریه سکه" },
];

/* ------------------------------------------------------------------
   ابزارهای کمکی برای ساخت متن اشتراک‌گذاری
------------------------------------------------------------------ */
const faDigits = ["۰", "۱", "۲", "۳", "۴", "۵", "۶", "۷", "۸", "۹"];
const toFaDigits = (input) =>
  String(input).replace(/[0-9]/g, (d) => faDigits[d]);

const formatRialFa = (value) => {
  const rounded = Math.round(value || 0);
  const grouped = rounded.toLocaleString("en-US"); // "173,000,000"
  return toFaDigits(grouped.replace(/,/g, "٬")) + " ریال";
};

const getPersianYear = () => {
  const parts = new Intl.DateTimeFormat("fa-IR-u-ca-persian", {
    year: "numeric",
  }).formatToParts(new Date());
  const year = parts.find((p) => p.type === "year")?.value || "";
  return toFaDigits(year);
};

const getPersianDateTimeText = () => {
  const now = new Date();
  const weekday = new Intl.DateTimeFormat("fa-IR", {
    weekday: "long",
  }).format(now);

  const dParts = new Intl.DateTimeFormat("fa-IR-u-ca-persian", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(now);
  const get = (type) => dParts.find((p) => p.type === type)?.value || "";

  const time = new Intl.DateTimeFormat("fa-IR", {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  }).format(now);

  return toFaDigits(
    `${weekday} ${get("year")}/${get("month")}/${get("day")} ساعت ${time}`,
  );
};

const SHARE_APP_FOOTER =
  "اپلیکیشن مدیریت دفتر وکیل و هوش مصنوعی حقوقی وکیلسان\nhttps://vakilsun.com";

const CASE_DESCRIPTIONS = {
  mali: "هزینه دادرسی محاکم دادگستری در مرحله بدوی تا مبلغ دویست میلیون ریال معادل دو و نیم درصد و نسبت به مازاد بر آن معادل سه و نیم درصد بهای خواسته محاسبه می‌گردد. در مراحل واخواهی و تجدیدنظر معادل چهار و نیم درصد و در مراحل اعاده دادرسی، فرجام‌خواهی و اعتراض ثالث معادل پنج و نیم درصد محکومٌ‌به یا بهای خواسته محاسبه می‌شود.",
  "gheyre-mali":
    "هزینه دادرسی دعاوی غیرمالی بر اساس نوع دعوا و مرحله رسیدگی، مطابق تعرفه‌های مصوب به‌صورت مقطوع تعیین می‌گردد.",
  "mehriyeh-sekeh":
    "هزینه دادرسی دعاوی مهریه سکه بر مبنای ارزش ریالی کل سکه‌های مورد مطالبه (بر اساس نرخ روز سکه بهار آزادی) و با اعمال درصدهای مقرر در هر مرحله محاسبه می‌شود.",
};

function buildShareText({
  caseType,
  rows,
  amount,
  shora,
  selectedNonFinancial,
  coinRows,
  coinCount,
  shoraCoin,
}) {
  const year = getPersianYear();
  const lines = [];

  lines.push("ورودی ها :");
  lines.push(`سال : ${year}`);

  if (caseType === "mali") {
    lines.push(`بهای خواسته یا محکومٌ به : ${formatRialFa(toNumber(amount))}`);
    lines.push(`مرجع رسیدگی شورای حل اختلاف است : ${shora ? "بله" : "خیر"}`);
  } else if (caseType === "gheyre-mali") {
    lines.push(`نوع دعوا : ${selectedNonFinancial.label}`);
  } else {
    lines.push(`تعداد سکه : ${toFaDigits(toNumber(coinCount))}`);
    lines.push(
      `مرجع رسیدگی شورای حل اختلاف است : ${shoraCoin ? "بله" : "خیر"}`,
    );
  }
  lines.push("");

  lines.push("نتیجه محاسبه :");
  if (caseType === "mali") {
    rows.forEach((r) => lines.push(`${r.label} : ${formatRialFa(r.value)}`));
  } else if (caseType === "gheyre-mali") {
    selectedNonFinancial.stages.forEach((s) =>
      lines.push(`${s.label} : ${formatRialFa(s.value)}`),
    );
  } else {
    coinRows.forEach((r) =>
      lines.push(`${r.label} : ${formatRialFa(r.value)}`),
    );
  }

  lines.push("");
  lines.push("مستندات :");
  lines.push(`قانون بودجه سال ${year} کل کشور`);

  lines.push("");
  lines.push("توضیحات :");
  lines.push(CASE_DESCRIPTIONS[caseType]);

  lines.push("");
  lines.push("زمان محاسبه :");
  lines.push(getPersianDateTimeText());

  lines.push("");
  lines.push(SHARE_APP_FOOTER);

  return lines.join("\n");
}

export default function FilingFeeCalculator() {
  const [caseType, setCaseType] = useState("mali");

  // دعاوی مالی
  const [amount, setAmount] = useState("");
  const [shora, setShora] = useState(false);

  // دعاوی غیرمالی
  const [nonFinancialKey, setNonFinancialKey] = useState(
    NON_FINANCIAL_TYPES[0].key,
  );

  // چک بلامحل
  const [checkAmount, setCheckAmount] = useState("");

  // مهریه سکه
  const [coinCount, setCoinCount] = useState("");
  const [shoraCoin, setShoraCoin] = useState(false);

  const [submitted, setSubmitted] = useState(false);

  const rows = useMemo(() => {
    const v = toNumber(amount);
    const shoraFactor = shora ? 0.5 : 1;
    return STAGES.map((s) => ({ ...s, value: s.calc(v) * shoraFactor }));
  }, [amount, shora]);

  const first = rows[0];
  const rest = rows.slice(1);

  const selectedNonFinancial = NON_FINANCIAL_TYPES.find(
    (t) => t.key === nonFinancialKey,
  );

  const checkFeeValue = feeBadCheck(toNumber(checkAmount));

  const coinRows = useMemo(() => {
    const totalValue = toNumber(coinCount) * COIN_VALUE_RIAL;
    const shoraFactor = shoraCoin ? 0.5 : 1;
    return COIN_STAGES.map((s) => ({
      ...s,
      value: s.calc(totalValue) * shoraFactor,
    }));
  }, [coinCount, shoraCoin]);
  const coinFirst = coinRows[0];
  const coinRest = coinRows.slice(1);

  const shareText = useMemo(
    () =>
      buildShareText({
        caseType,
        rows,
        amount,
        shora,
        selectedNonFinancial,
        coinRows,
        coinCount,
        shoraCoin,
      }),
    [
      caseType,
      rows,
      amount,
      shora,
      selectedNonFinancial,
      coinRows,
      coinCount,
      shoraCoin,
    ],
  );

  return (
    <div className="py-24">
      <Header title={"محاسبه‌گر"}>
        <HeaderRobotsBtn />
        <HeaderSupportBtn />
        <HeaderProfileBtn />
      </Header>

      <div className="max-w-2xl mx-auto px-4 sm:px-6 py-8 sm:py-10 space-y-6">
        {/* header */}
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-full flex items-center justify-center shrink-0 bg-neutral-800">
            <Scale className="w-5 h-5 text-violet-200" />
          </div>
          <div>
            <h1 className="text-lg sm:text-xl font-extrabold text-neutral-800">
              محاسبه‌گر هزینه دادرسی
            </h1>
            <p className="text-xs sm:text-sm text-neutral-600">
              دعاوی مالی، غیرمالی، چک بلامحل و مهریه سکه — در تمام مراحل
            </p>
          </div>
        </div>

        {/* form card */}
        <div className="rounded-2xl p-5 sm:p-6 space-y-5 bg-white border border-primary/20">
          <Field label="نوع دعوا">
            <select
              value={caseType}
              onChange={(e) => {
                setCaseType(e.target.value);
                setSubmitted(false);
              }}
              style={selectStyle}
            >
              {CASE_TYPES.map((t) => (
                <option key={t.key} value={t.key}>
                  {t.label}
                </option>
              ))}
            </select>
          </Field>

          {caseType === "mali" && (
            <>
              <Field label="بهای خواسته یا محکوم‌به (ریال)">
                <input
                  type="text"
                  inputMode="numeric"
                  value={amount}
                  onChange={(e) => setAmount(formatAmountInput(e.target.value))}
                  placeholder="مثلاً 50,000,000"
                  style={inputStyle}
                />
              </Field>

              <label className="flex items-center gap-2 text-sm cursor-pointer select-none text-neutral-800">
                <input
                  type="checkbox"
                  checked={shora}
                  onChange={(e) => setShora(e.target.checked)}
                  className="w-4 h-4 accent-primary"
                />
                دعوا در شورای حل اختلاف مطرح است
              </label>
            </>
          )}

          {caseType === "gheyre-mali" && (
            <Field label="نوع دعوا غیرمالی">
              <select
                value={nonFinancialKey}
                onChange={(e) => setNonFinancialKey(e.target.value)}
                style={selectStyle}
              >
                {NON_FINANCIAL_TYPES.map((t) => (
                  <option key={t.key} value={t.key}>
                    {t.label}
                  </option>
                ))}
              </select>
            </Field>
          )}

          {caseType === "mehriyeh-sekeh" && (
            <>
              <Field label="تعداد سکه">
                <input
                  type="text"
                  inputMode="numeric"
                  value={coinCount}
                  onChange={(e) =>
                    setCoinCount(formatAmountInput(e.target.value))
                  }
                  placeholder="مثلاً 4"
                  style={inputStyle}
                />
              </Field>

              <label className="flex items-center gap-2 text-sm cursor-pointer select-none text-neutral-800">
                <input
                  type="checkbox"
                  checked={shoraCoin}
                  onChange={(e) => setShoraCoin(e.target.checked)}
                  className="w-4 h-4 accent-primary"
                />
                محاسبه براساس طرح دعوا در شورای حل اختلاف
              </label>
            </>
          )}

          <button
            onClick={() => setSubmitted(true)}
            className="w-full rounded-xl py-3 font-bold text-sm sm:text-[15px] transition-opacity hover:opacity-90 bg-primary text-white"
          >
            محاسبه
          </button>
        </div>

        {/* results */}
        {submitted && (
          <div className="rounded-2xl p-6 space-y-5 bg-neutral-800">
            <ShareButton text={shareText} iconOnly="false" variant="ghost" />
            {caseType === "mali" && (
              <>
                <ResultBox
                  label={first.label}
                  value={`${fmt(first.value)} ریال`}
                  wide
                />
                <div className="grid sm:grid-cols-2 gap-4">
                  {rest.map((r) => (
                    <ResultBox
                      key={r.key}
                      label={r.label}
                      value={`${fmt(r.value)} ریال`}
                    />
                  ))}
                </div>
                {shora && (
                  <div className="flex items-start gap-2 text-xs text-neutral-300">
                    <Info
                      size={14}
                      className="mt-0.5 shrink-0 text-neutral-300"
                    />
                    <span>
                      ارقام بالا با احتساب نصف هزینه دادرسی دادگاه عمومی محاسبه
                      شده، چون دعوا در شورای حل اختلاف مطرح است.
                    </span>
                  </div>
                )}
              </>
            )}

            {caseType === "gheyre-mali" && (
              <>
                <ResultBox
                  label={selectedNonFinancial.stages[0].label}
                  value={`${fmt(selectedNonFinancial.stages[0].value)} ریال`}
                  wide
                />
                <div className="grid sm:grid-cols-2 gap-4">
                  {selectedNonFinancial.stages.slice(1).map((s) => (
                    <ResultBox
                      key={s.label}
                      label={s.label}
                      value={`${fmt(s.value)} ریال`}
                    />
                  ))}
                </div>
              </>
            )}

            {caseType === "mehriyeh-sekeh" && (
              <>
                <ResultBox
                  label={coinFirst.label}
                  value={`${fmt(coinFirst.value)} ریال`}
                  wide
                />
                <div className="grid sm:grid-cols-2 gap-4">
                  {coinRest.map((r) => (
                    <ResultBox
                      key={r.key}
                      label={r.label}
                      value={`${fmt(r.value)} ریال`}
                    />
                  ))}
                </div>
                {shoraCoin && (
                  <div className="flex items-start gap-2 text-xs text-neutral-300">
                    <Info
                      size={14}
                      className="mt-0.5 shrink-0 text-neutral-300"
                    />
                    <span>
                      ارقام بالا با احتساب نصف هزینه دادرسی دادگاه عمومی محاسبه
                      شده، چون دعوا در شورای حل اختلاف مطرح است.
                    </span>
                  </div>
                )}
              </>
            )}

            <p className="text-[11px] leading-5 text-neutral-300">
              ارقام صرفاً جنبه راهنما دارد و ملاک نهایی، محاسبه واحد ثبت دادگاه
              است.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
